import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { Newspaper, PlayCircle, ArrowRight } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { supabase } from '../lib/supabase';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  cover_image_url: string | null;
  author_name: string | null;
  published_at: string | null;
  tags: string[] | null;
  post_type: 'article' | 'vlog' | null;
}

function fmtDate(dateStr: string | null): string {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('es-GT', { day: '2-digit', month: 'short', year: 'numeric' });
}

export const NovedadesPage: React.FC = () => {
  const { theme } = useTheme();
  const isLight = theme !== 'dark';
  const navigate = useNavigate();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!supabase) { setLoading(false); return; }
    let cancelled = false;
    supabase
      .from('blog_posts')
      .select('id,title,slug,excerpt,cover_image_url,author_name,published_at,tags,post_type')
      .eq('status', 'published')
      .eq('show_in_crm', true)
      .order('published_at', { ascending: false, nullsFirst: false })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (!error && data) setPosts(data as BlogPost[]);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="animate-page-entry max-w-4xl mx-auto">
      <div className="mb-6">
        <h3 className="text-xl sm:text-2xl font-black tracking-tight text-primary">Novedades</h3>
        <p className="text-sm text-secondary mt-1">Anuncios de producto, nuevas funciones y consejos de Intercambio Inmobiliario.</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : posts.length === 0 ? (
        <div className={clsx('text-center py-16 rounded-2xl border', isLight ? 'bg-white border-slate-100 text-slate-400' : 'glass-panel border-white/5 text-slate-500')}>
          <Newspaper size={40} className="mx-auto mb-3 opacity-40" />
          <p className="font-semibold">Aún no hay novedades</p>
          <p className="text-sm mt-1">Pronto publicaremos anuncios y consejos aquí.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {posts.map((post) => {
            const isVlog = post.post_type === 'vlog';
            return (
              <button
                key={post.id}
                onClick={() => navigate(`/novedades/${post.slug}`)}
                className={clsx(
                  'text-left rounded-2xl border overflow-hidden transition-all group flex flex-col',
                  isLight ? 'bg-white border-slate-100 shadow-sm hover:shadow-md' : 'glass-panel border-white/5 hover:border-white/10'
                )}
              >
                <div className={clsx('relative aspect-video overflow-hidden', isLight ? 'bg-slate-100' : 'bg-white/5')}>
                  {post.cover_image_url ? (
                    <img src={post.cover_image_url} alt={post.title} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      {isVlog ? <PlayCircle size={40} className="text-slate-300" /> : <Newspaper size={40} className="text-slate-300" />}
                    </div>
                  )}
                  {isVlog && (
                    <span className="absolute inset-0 flex items-center justify-center bg-black/25">
                      <PlayCircle size={44} className="text-white" />
                    </span>
                  )}
                </div>
                <div className="p-4 flex flex-col flex-1">
                  {post.tags?.length ? (
                    <span className={clsx('self-start text-[10px] font-bold px-2 py-0.5 rounded-chip uppercase mb-2', isLight ? 'bg-slate-100 text-slate-600' : 'bg-white/10 text-slate-300')}>
                      {post.tags[0]}
                    </span>
                  ) : null}
                  <h4 className={clsx('font-bold leading-snug group-hover:text-indigo-500 transition-colors', isLight ? 'text-slate-900' : 'text-white')}>
                    {post.title}
                  </h4>
                  {post.excerpt && <p className="text-sm text-slate-500 mt-1 line-clamp-2 flex-1">{post.excerpt}</p>}
                  <p className="text-xs text-indigo-500 font-semibold mt-3 flex items-center gap-1">
                    {isVlog ? 'Ver video' : 'Leer más'} <ArrowRight size={12} />
                    <span className="text-slate-400 font-normal ml-1">· {fmtDate(post.published_at)}</span>
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
